'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/helpers/utils';
import IconButton from './IconButton';

type PopupProps = {
    className?: string;
    onClose: () => void;
    active: boolean;
    ariaLabel?: string;
    children: ReactNode;
};

const FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

export default function Popup({ className = '', onClose, active, ariaLabel, children }: PopupProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<Element | null>(null);

    // Esc to close (only while open).
    useEffect(() => {
        if (!active) return;
        const handleKeyUp = ({ key }: KeyboardEvent) => {
            if (key === 'Escape') onClose();
        };
        window.addEventListener('keyup', handleKeyUp);
        return () => window.removeEventListener('keyup', handleKeyUp);
    }, [active, onClose]);

    // Move focus into the dialog on open; restore it to the opener on close.
    useEffect(() => {
        if (active) {
            openerRef.current = document.activeElement;
            panelRef.current?.focus();
        } else if (openerRef.current instanceof HTMLElement) {
            openerRef.current.focus();
            openerRef.current = null;
        }
    }, [active]);

    // Trap Tab focus within the dialog while open.
    useEffect(() => {
        if (!active) return;
        const panel = panelRef.current;
        if (!panel) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
                (el) => el.getClientRects().length > 0
            );
            if (focusable.length === 0) {
                e.preventDefault();
                panel.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const current = document.activeElement;
            if (e.shiftKey) {
                if (current === first || current === panel) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (current === last) {
                e.preventDefault();
                first.focus();
            }
        };

        panel.addEventListener('keydown', handleKeyDown);
        return () => panel.removeEventListener('keydown', handleKeyDown);
    }, [active]);

    return (
        <div
            className={cn(
                'fixed top-0 left-0 w-full h-full flex px-4 accent-purple-800 pointer-events-none z-50 transition-all duration-300',
                active ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            )}
        >
            <div className="absolute top-0 left-0 w-full h-full bg-white/[0.85]" onClick={onClose} />

            {active && (
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel}
                    tabIndex={-1}
                    className={cn(
                        'relative m-auto w-full p-4 md:p-7 rounded-[35px] flex flex-col items-end focus:outline-none',
                        className
                    )}
                >
                    <IconButton icon="close" onClick={onClose} className="shrink-0" />

                    <div className="w-full flex-1">{children}</div>
                </div>
            )}
        </div>
    );
}
